import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageSquare, Bot, PenTool, ShieldCheck, MousePointerClick, CheckCircle2 } from 'lucide-react';
export function OperatePlainLanguagePage() {
  const steps = [{
    title: 'Ask a real question',
    desc: 'Use natural language to search, summarize, or plan.',
    icon: MessageSquare
  }, {
    title: 'Get a grounded answer',
    desc: 'Booker responds from the live workflow, not a separate assistant silo.',
    icon: Bot
  }, {
    title: 'Draft or prepare the action',
    desc: 'Booker can generate outreach, summarize offers, or prep contracts.',
    icon: PenTool
  }, {
    title: 'Review with control intact',
    desc: 'Important actions still honor approvals and policy boundaries.',
    icon: ShieldCheck
  }, {
    title: 'Move back into the UI when needed',
    desc: 'Open the exact screen to fine-tune, approve, or complete the workflow.',
    icon: MousePointerClick
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7316">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7317">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7318">
          Use Booker with clicks when you want precision — and plain language
          when you want speed.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7319">
          Ask Booker to plan routes, draft outreach, summarize offers, check
          lineup conflicts, or prepare documents. Same workflow engine. Faster
          control surface.
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7320">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7321">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7322">
            Not every question should take five screens and ten clicks.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7323">
            Sometimes users want to work visually. Sometimes they just want the
            answer, the summary, or a prepared action. Booker supports both.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7324">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7325">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7326"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7327">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7328">
              A manager is between meetings and does not want to click through
              five screens to answer one question. They just want to know which
              June offers are below the fee floor, whether the lineup can cover
              a weekend run, and what still needs to happen to lock the dates.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7329">
              Booker can answer, summarize, and prepare actions in plain
              language — while still handing off to the structured UI for review
              and approval.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7330">
        <div className="space-y-6" data-id="element-7331">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7332">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7333">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7334" />
              </div>
              <div data-id="element-7335">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7336">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7337">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Prompt Examples */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7338">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12" data-id="element-7339">
          <h2 className="text-2xl font-bold text-textMain mb-8 text-center" data-id="element-7340">
            Try asking Booker:
          </h2>
          <div className="grid gap-4" data-id="element-7341">
            {['Plan a June folk run from Michigan to Maine.', 'Find the best 25 venues for this route.', 'Summarize all offers below our minimum.', 'Check whether the full lineup is available.', 'Draft contracts for every confirmed date.'].map((prompt, i) => <div key={i} className="p-4 bg-background border border-border rounded-xl text-textMain font-medium flex items-center gap-3" data-id="element-7342">
                <MessageSquare className="w-5 h-5 text-primary" data-id="element-7343" />"{prompt}"
              </div>)}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7344">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center" data-id="element-7345">
          <h2 className="text-2xl font-bold text-textMain" data-id="element-7346">
            Less clicking for routine work. More control where it matters.
          </h2>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7347">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7348">
          <Link to="/ai-assistant" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7349">
            See sample workflows
          </Link>
          <Link to="/onboarding" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7350">
            Start free
          </Link>
        </div>
      </section>
    </div>;
}